import { type ReactNode, useState } from 'react';
import { Button, Typography } from '@mui/material';

import type { IntakePracticeVerification } from '../../types';

import {
  type IntakeJourneyContent,
  IntakeJourneyShell,
  type IntakeJourneyStage,
  type IntakeJourneyStageState,
} from './IntakeJourneyShell';
import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import { IntakeKickoffCard } from './IntakeKickoffCard';
import {
  effectiveIntakeStatusTab,
  EhrImportComingSoon,
  INTAKE_STATUS_TAB_LABELS,
  INTAKE_STATUS_TABS,
  IntakeStatusDashboard,
  type IntakeStatusTab,
} from './IntakeStatusDashboard';
import { IntakeVerificationStatus } from './IntakeVerificationStatus';

export interface IntakeJourneyProgress {
  /** Agreements & setup counts as done. Supplied by the agreement owner. */
  agreementsDone: boolean;
  agreementsCaption: string;
  /** Current provider's intake percent complete, from the live form. */
  intakePercent: number;
  /** Caller supplies server-confirmed verification for the displayed scope. */
  verificationDone?: boolean;
  practiceVerification?: IntakePracticeVerification | null;
  /** Intake Status tracks from the saved services. Omit to show every track. */
  statusTabs?: readonly IntakeStatusTab[];
}

function statusCaption(tabs: readonly IntakeStatusTab[]): string {
  return tabs.map(tab => INTAKE_STATUS_TAB_LABELS[tab]).join(' · ');
}

/** The design's lock ladder, from the signals we actually have. Staff embeds pass no progress. */
export function journeyStagesFromProgress(
  progress: IntakeJourneyProgress,
  _payerEnabled: boolean,
): Record<IntakeJourneyStage, IntakeJourneyStageState> {
  const intakeDone = progress.intakePercent >= 100;
  return {
    agreements: {
      statusLabel: progress.agreementsDone ? 'Services saved' : 'In progress',
      caption: progress.agreementsCaption,
      tone: 'sug',
    },
    intake: intakeDone
      ? { statusLabel: 'Complete', caption: 'Everything we needed is in', completed: true }
      : {
          statusLabel: 'In progress',
          caption: `${Math.round(progress.intakePercent)}% complete`,
          tone: 'sug',
        },
    verification: progress.verificationDone
      ? { statusLabel: 'Verified', caption: 'Your intake has been verified', completed: true }
      : progress.practiceVerification
        ? {
            statusLabel:
              progress.practiceVerification.state === 'awaiting' ? 'In review' : 'Incomplete',
            caption: `${progress.practiceVerification.provider_counts.verified} of ${progress.practiceVerification.provider_counts.total} providers verified`,
            tone: 'info',
          }
        : !intakeDone
          ? { statusLabel: 'Locked', lockedReason: 'Opens when your intake is in.' }
          : { statusLabel: 'In review', caption: 'Usually one business day', tone: 'info' },
    'go-live': {
      statusLabel: 'View status',
      caption: statusCaption(progress.statusTabs ?? INTAKE_STATUS_TABS),
      tone: 'info',
    },
  };
}

/**
 * Completion remains owned by each live feature. The owner must key this tree by
 * stable practice/auth identity on scope changes, preserving the key during refetch.
 */
export function LiveIntakeJourney({
  enabled = true,
  selectedStage,
  onNavigate,
  intake,
  payers,
  credentialing,
  credentialingAvailable,
  verification,
  ehrImport,
  keepStatusPanelsMounted = false,
  agreements,
  progress,
  payerEnabled,
  onKickoffClick,
  agreementContent,
  agreementStatusLabel,
  selectedStatusTab,
  onSelectStatusTab,
}: {
  selectedStatusTab?: IntakeStatusTab;
  onSelectStatusTab?: (tab: IntakeStatusTab) => void;
  enabled?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  /** Return a stable form tree and pass effective activity through; do not conditionally mount it. */
  intake: IntakeJourneyContent;
  payers: ReactNode;
  credentialing?: ReactNode;
  credentialingAvailable?: boolean;
  verification?: ReactNode;
  ehrImport?: ReactNode;
  /** Staff workflow drafts survive service changes. Customer gates remain unchanged. */
  keepStatusPanelsMounted?: boolean;
  /** Live Agreements & setup content. Omit for the read-only placeholder (staff embed). */
  agreements?: ReactNode;
  /** Live signals for the lock ladder. Omit to keep every stage open (staff preview). */
  progress?: IntakeJourneyProgress;
  payerEnabled: boolean;
  onKickoffClick?: () => void;
  agreementContent?: ReactNode;
  /** Caller-owned presentation, including confirmed completion; never inferred from SDK events. */
  agreementStatusLabel?: string;
}): React.JSX.Element {
  const [localStatusTab, setLocalStatusTab] = useState<IntakeStatusTab>('billing');
  const statusTabs = progress?.statusTabs ?? INTAKE_STATUS_TABS;
  const statusTab = effectiveIntakeStatusTab(selectedStatusTab ?? localStatusTab, statusTabs);
  const billingLockedReason = !payerEnabled
    ? 'Payer connections are not enabled.'
    : progress && !progress.practiceVerification && !(progress.intakePercent >= 100)
      ? 'Complete your intake form before billing setup can begin.'
      : progress && !progress.verificationDone
        ? 'Opens after JotPsych verifies your intake.'
        : undefined;
  const billingStatus = !payerEnabled
    ? 'Unavailable'
    : billingLockedReason
      ? 'Locked'
      : 'View current status';
  const stages = progress
    ? journeyStagesFromProgress(progress, payerEnabled)
    : {
        agreements: { statusLabel: agreements ? 'Available' : 'Coming soon' },
        intake: { statusLabel: 'Available' },
        verification: { statusLabel: verification ? 'Review providers' : 'Coming soon' },
        'go-live': { statusLabel: 'View status', caption: statusCaption(INTAKE_STATUS_TABS) },
      };
  if (agreementStatusLabel)
    stages.agreements = { ...stages.agreements, statusLabel: agreementStatusLabel };
  return (
    <IntakeJourneyShell
      showJourneyChrome={enabled}
      selectedStage={selectedStage}
      onNavigate={onNavigate}
      stages={stages}
      // The live setup rail carries its own kickoff step.
      kickoff={agreements ? undefined : <IntakeKickoffCard onScheduleClick={onKickoffClick} />}
      content={{
        agreements: agreements ?? agreementContent ?? (
          <IntakeJourneyStatus
            title="Your agreements will appear here"
            statusLabel="DocuSign signing coming soon"
            action={
              <Button variant="contained" onClick={() => onNavigate('intake')}>
                Continue to intake
              </Button>
            }
          >
            <Typography>
              You’ll be able to review and sign your practice agreements here when signing is ready.
              You can continue your intake now.
            </Typography>
          </IntakeJourneyStatus>
        ),
        intake,
        verification:
          verification ??
          (progress ? (
            <IntakeVerificationStatus
              verified={progress.verificationDone}
              practiceVerification={progress.practiceVerification}
            />
          ) : (
            <IntakeJourneyStatus
              title="Practice review status is coming soon"
              statusLabel="Not available yet"
              action={
                <Button variant="outlined" onClick={() => onNavigate('intake')}>
                  Return to intake
                </Button>
              }
            >
              <Typography>
                Your existing intake and review tools remain available. This page does not yet show
                whether your practice has been verified.
              </Typography>
            </IntakeJourneyStatus>
          )),
        'go-live': !enabled ? (
          payerEnabled ? (
            payers
          ) : null
        ) : (
          <IntakeStatusDashboard
            keepPanelsMounted={keepStatusPanelsMounted}
            selectedTab={statusTab}
            onSelectTab={onSelectStatusTab ?? setLocalStatusTab}
            availableTabs={statusTabs}
            billingStatus={billingStatus}
            credentialing={credentialing}
            credentialingAvailable={credentialingAvailable}
            credentialingLockedReason={
              progress && !(progress.intakePercent >= 100)
                ? 'Complete your intake form before credentialing can begin.'
                : undefined
            }
            billing={
              billingLockedReason ? (
                <IntakeJourneyStatus title="Billing" statusLabel={billingStatus}>
                  <Typography>{billingLockedReason}</Typography>
                </IntakeJourneyStatus>
              ) : (
                payers
              )
            }
            ehrImport={ehrImport ?? <EhrImportComingSoon />}
          />
        ),
      }}
    />
  );
}
